import PercentIcon from '@mui/icons-material/Percent';
import { Grid2, useTheme } from '@mui/material';
import { type FC, memo, useState } from 'react';
import AnimatedNumber from 'react-animated-numbers';
import type { IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { AvaniAnimatedNumber } from './AnimatedNumber';

interface AvaniNumberSpinProps extends IIsPdf {
  numberValue: number;
  preserveTailing?: boolean;
  roundUp?: boolean;
  isPercentage?: boolean;
  fSize?: number;
  sx?: any;
  clickTrigger?: number;
  onClickTrigger?: () => void;
  isRanking?: boolean;
}

const AvaniNumberSpinComponent: FC<AvaniNumberSpinProps> = ({
  numberValue,
  roundUp = false,
  preserveTailing = false,
  isPercentage = false,
  fSize,
  sx,
  isPDF = false,
  clickTrigger: externalClickTrigger,
  onClickTrigger,
  isRanking = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const [internalClickTrigger, setInternalClickTrigger] = useState(0);

  const clickTrigger = externalClickTrigger !== undefined ? externalClickTrigger : internalClickTrigger;
  const output = roundUp ? Math.round(numberValue) : numberValue;

  return (
    <Grid2
      container
      justifyContent={isRanking ? 'start' : 'end'}
      alignItems="end"
      onClick={() => {
        if (!isPDF) {
          if (onClickTrigger) {
            onClickTrigger();
          } else {
            setInternalClickTrigger((prev) => prev + 1);
          }
        }
      }}
      style={{ ...sx, cursor: !isPDF ? 'pointer' : 'default', marginLeft: isRanking ? 16 : undefined }}
    >
      <Grid2 key={clickTrigger === 0 ? 'spin-initial' : `spin-${clickTrigger}`}>
        {isPDF ? (
          <span
            style={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 'bold',
              color: theme.colors.avani.turc,
              fontSize: fSize ? fSize : 16
            }}
          >
            {preserveTailing ? output.toFixed(2) : output}
          </span>
        ) : preserveTailing ? (
          <AvaniAnimatedNumber
            fontStyle={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 'bold',
              color: theme.colors.avani.turc,
              fontSize: fSize ? fSize : 16
            }}
            includeComma
            animateToNumber={output.toFixed(2)}
          />
        ) : (
          <AnimatedNumber
            fontStyle={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 'bold',
              color: theme.colors.avani.turc,
              fontSize: fSize ? fSize : 16
            }}
            includeComma
            animateToNumber={output}
          />
        )}
      </Grid2>
      {isPercentage && (
        <Grid2>
          <PercentIcon
            style={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 'bold',
              color: theme.colors.avani.turc,
              fontSize: fSize ? fSize : 16
            }}
          />
        </Grid2>
      )}
    </Grid2>
  );
};

export const AvaniNumberSpin = memo(AvaniNumberSpinComponent, (prevProps, nextProps) => {
  // Only re-render if these specific props change (excluding sx since it's just styling)
  return (
    prevProps.numberValue === nextProps.numberValue &&
    prevProps.roundUp === nextProps.roundUp &&
    prevProps.preserveTailing === nextProps.preserveTailing &&
    prevProps.isPercentage === nextProps.isPercentage &&
    prevProps.fSize === nextProps.fSize &&
    prevProps.isPDF === nextProps.isPDF &&
    prevProps.clickTrigger === nextProps.clickTrigger &&
    prevProps.onClickTrigger === nextProps.onClickTrigger
    // Note: sx is excluded - it's just styling and shouldn't trigger re-renders
  );
});
